/**
 * The candidate report.
 *
 * Two things make this different from UnifiedReport:
 *
 * 1. Every number comes from `evaluation_details` by way of reportModel. There
 *    is no scoring logic in this file at all, and nothing here reads
 *    `test_sessions.answers`, the browser-written copy that used to make the
 *    header and the per-question badges disagree. A page that cannot compute a
 *    score cannot compute a wrong one.
 *
 * 2. It is written for the person who makes the hire. Summary is plain language
 *    with no jargon; Full detail carries the questions, the code and the
 *    proctoring log for whoever wants to check the working.
 *
 * Presentation comes from report.css, lifted verbatim from the approved design
 * and scoped under .rpt. Styling this with utility classes by hand drifted from
 * the design once already, so the stylesheet is the source of truth.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import ContactDialog, { type ContactDetails, type ContactIntent } from "./ContactDialog";
import { Icon, IconSprite } from "./Icons";
import { SkillLogo, hasSkillLogo } from "./SkillLogo";
import {
  buildReportModel, checkModel, deriveNarrative, formatDuration, parseEvaluation,
} from "@/lib/reportModel";
import {
  bandFor, conceptIconFor, countNoise, deriveFindings, firstNameOf, hasRecordedAnswer, maskSurname,
  missingAnswerStatus, questionTypeLabel, radarGeometry, skillBlurb, splitSubmission,
  summariseEvents, toneFor,
} from "./reportPresenters";
import "./report.css";

interface Props {
  session: any;
  report: any;
  questions?: any[];
  candidateAnswers?: any[];
  proctoringEvents?: any[];
  isPublic?: boolean;
}

// The marketing site, which is what the footer promotes.
const TALENT_NAVIGATOR = "https://talentnavigator.in";
// The signed-in product, which is a different host. Pointing the interview
// button at the marketing site sent every client to a 404, because
// /dashboard/* only exists here.
const TALENT_NAVIGATOR_APP = "https://app.weremotedevs.com";
const SKILLPROOF = "https://test.weremotedevs.com";

/** Tone to the palette class the stylesheet defines. */
const P = { good: "p-pass", mid: "p-warn", bad: "p-fail", neutral: "p-mute" } as const;
const V = { good: "var(--pass)", mid: "var(--warn)", bad: "var(--fail)", neutral: "var(--text-2)" } as const;
const BAR = { good: "var(--pass-b)", mid: "var(--warn-b)", bad: "var(--fail-b)", neutral: "var(--border-2)" } as const;
const IC = { good: "check", mid: "half", bad: "x", neutral: "target" } as const;
type Tone = keyof typeof P;

const CONCEPT_ICON: Record<string, string> = {
  brain: "bulb", layers: "layers", chat: "msg", wrench: "tools", database: "layers",
  server: "code", blueprint: "blueprint", shield: "shield", users: "user", target: "target",
};

const SkillTile = ({ skill }: { skill: string }) =>
  hasSkillLogo(skill) ? (
    <span className="slogo brandtile"><SkillLogo skill={skill} size={23} /></span>
  ) : (
    <span className="slogo" style={{ ["--bc" as any]: "#5B4BFF" }}>
      <Icon id={CONCEPT_ICON[conceptIconFor(skill)] ?? "target"} />
    </span>
  );

export default function ReportV2({
  session, report, questions = [], candidateAnswers = [], proctoringEvents = [], isPublic = true,
}: Props) {
  const [full, setFull] = useState(false);
  const [nudged, setNudged] = useState(false);
  const [openQ, setOpenQ] = useState<string | null>(null);
  const [showHarness, setShowHarness] = useState(false);
  const [lightbox, setLightbox] = useState<any>(null);
  const [active, setActive] = useState("who");
  const [sent, setSent] = useState(false);
  const [asking, setAsking] = useState<ContactIntent | null>(null);

  const evaluation = useMemo(() => parseEvaluation(report?.evaluation_details), [report]);
  const model = useMemo(
    () => (evaluation ? buildReportModel(evaluation, { passMark: session?.passing_threshold }) : null),
    [evaluation, session?.passing_threshold],
  );
  const problems = useMemo(() => (model ? checkModel(model) : ["this report has no evaluation to show"]), [model]);
  // Evidence of an attempt only; the report never renders answer text from this browser-written copy.
  const sessionAnswers = useMemo(() => {
    let a = session?.answers;
    if (typeof a === "string") { try { a = JSON.parse(a); } catch { a = null; } }
    return a && typeof a === "object" && !Array.isArray(a) ? (a as Record<string, unknown>) : {};
  }, [session?.answers]);
  const inSession = (id: any) => id != null && hasRecordedAnswer(sessionAnswers[id]);

  const profile = useMemo(() => {
    const p = session?.candidate_profile;
    if (!p) return {} as any;
    if (typeof p === "string") { try { return JSON.parse(p); } catch { return {}; } }
    return p;
  }, [session]);

  const firstName = firstNameOf(session?.candidate_name);
  const narrative = useMemo(() => (model ? deriveNarrative(model, firstName) : null), [model, firstName]);
  const findings = useMemo(
    () => (model ? deriveFindings(model, { cvSkills: profile?.mergedSkills ?? [] }) : []),
    [model, profile],
  );
  const shots = useMemo(() => buildShots(profile, proctoringEvents), [profile, proctoringEvents]);
  const events = useMemo(() => summariseEvents(proctoringEvents), [proctoringEvents]);
  const noise = useMemo(() => countNoise(proctoringEvents), [proctoringEvents]);
  const integrity = Number(report?.test_integrity_score ?? 100);
  const codingRows = useMemo(
    () => (model?.rows ?? []).filter((r) => String(r.question_type) === "coding"), [model]);

  const NAV = [
    ["who", "Candidate", "user"], ["call", "Recommendation", "bulb"],
    ["found", "What we found", "target"], ["skills", "Skills", "chart"],
    ["trust", "Proof", "shield"],
    ...(full ? [["answers", "Every answer", "doc"], ["code", "Their code", "code"]] : []),
    ["next", "Next step", "cal"],
  ] as [string, string, string][];

  useEffect(() => {
    const ids = NAV.map((n) => n[0]);
    const obs = new IntersectionObserver((es) => {
      const seen = es.filter((e) => e.isIntersecting).map((e) => e.target.id);
      if (seen.length) setActive(ids.find((i) => seen.includes(i)) ?? seen[0]);
    }, { rootMargin: "-12% 0px -70% 0px" });
    ids.forEach((i) => { const el = document.getElementById(i); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [full]);

  if (!model || problems.length > 0) {
    return (
      <div className="rpt" style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
        <IconSprite />
        <div className="card pad" style={{ maxWidth: 520, textAlign: "center" }}>
          <h2 className="sec" style={{ marginTop: 0 }}>This report needs regenerating</h2>
          <p className="sub" style={{ marginBottom: 0 }}>
            Its scores do not add up, so we are not showing you numbers we cannot stand behind.
            Nothing is lost; regenerating rebuilds the report from the graded answers.
          </p>
          {!isPublic && (
            <ul style={{ textAlign: "left", fontSize: 12.5, color: "var(--text-3)", marginTop: 14 }}>
              {problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
        </div>
      </div>
    );
  }

  const scoreTone: Tone = model.passed ? "good" : model.percentage >= model.passMark - 10 ? "mid" : "bad";
  const candidateId = session?.talent_os_candidate_id || profile?.candidateId || null;
  // One link shape for every kind of candidate, resolved on the other side.
  //
  // This used to point straight at /dashboard/candidate/<id>, which is a pool
  // profile. A candidate a client uploaded themselves has no pool profile --
  // their id is a roster row -- so every manually vetted candidate got a dead
  // link. /vetting/go knows which is which, and sends the client to the roster
  // row or to the real profile once that person has onboarded.
  //
  // Talent Navigator sends anyone not signed in to /auth and back again, so the
  // candidate stays the destination across the login.
  const interviewUrl = candidateId
    ? `${TALENT_NAVIGATOR_APP}/vetting/go?candidate=${encodeURIComponent(candidateId)}&action=request-interview`
    : `${TALENT_NAVIGATOR_APP}/dashboard/search`;

  const go = (id: string) => {
    if ((id === "answers" || id === "code") && !full) { setFull(true); setNudged(true); }
    requestAnimationFrame(() =>
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  return (
    <div className={`rpt${full ? " detail" : ""}${nudged ? " nudged" : ""}`}>
      <IconSprite />

      <div className="band">
        <div className="wrap">
          <a href={SKILLPROOF} target="_blank" rel="noopener noreferrer">
            <img src="/skillproof-logo.png" alt="SkillProof by WeRemoteDevs" />
          </a>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn btn-ghost" type="button"
              onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied"); }}>
              <Icon id="copy" />Copy link
            </button>
            <button className="btn btn-ghost" type="button" onClick={() => window.print()}>
              <Icon id="dl" />PDF
            </button>
          </div>
        </div>
      </div>

      <div className="modebar">
        <div className="wrap">
          <div style={{ display: "flex", alignItems: "center", gap: 13, flexWrap: "wrap" }}>
            <div className="seg" role="group" aria-label="Report detail level">
              <button type="button" aria-pressed={!full} onClick={() => setFull(false)}>
                <Icon id="eye" />Summary
              </button>
              <button type="button" aria-pressed={full} onClick={() => setFull(true)}>
                <Icon id="list" />Full detail
              </button>
            </div>
            <span className="modehint">
              {full ? "Every question, their code, and the full proctoring log." : "The decision, in plain language."}
            </span>
          </div>
        </div>
      </div>

      {!full && !nudged && (
        <div className="nudge">
          <div className="wrap">
            <Icon id="list" />
            <p>
              <b>Reviewing this as a developer?</b>
              <span className="long"> Switch to <b>Full detail</b> for every question, the code they wrote, and the complete proctoring log.</span>
            </p>
            <div className="nb">
              <button className="btn btn-primary" type="button" onClick={() => { setFull(true); setNudged(true); }}>Full detail</button>
              <button className="btn btn-outline" type="button" onClick={() => setNudged(true)}>No thanks</button>
            </div>
          </div>
        </div>
      )}

      <div className="shell">
        <nav className="rail" aria-label="Report sections">
          <p className="railh">This report</p>
          <ol>
            {NAV.map(([id, label, ic]) => (
              <li key={id} className={id === "answers" || id === "code" ? "d-nav" : undefined}>
                <a href={`#${id}`} aria-current={active === id ? "true" : "false"}
                  onClick={(e) => { e.preventDefault(); go(id); }}>
                  <Icon id={ic} /><span>{label}</span>
                </a>
              </li>
            ))}
          </ol>
          <button className="railcta" type="button" onClick={() => { setFull(true); setNudged(true); }}>
            <Icon id="list" />
            <span><b>See full detail</b>Every question, their code, the full log.</span>
          </button>
        </nav>

        <main>
          {/* -------------------------------------------------------- who */}
          <section id="who">
            <div className="card hero">
              <div className="avatar">
                {profile?.avatarUrl
                  ? <img src={profile.avatarUrl} alt={`Photo taken when ${firstName} started the assessment`} />
                  : <div style={{ width: "100%", height: "100%", borderRadius: 16, background: "var(--soft-2)", display: "grid", placeItems: "center" }}><Icon id="user" /></div>}
                {profile?.avatarUrl && <span className="vb" title="Identity verified"><Icon id="check" /></span>}
              </div>
              <div>
                <h1 className="hname">{maskSurname(session?.candidate_name, isPublic)}</h1>
                <p className="hrole">
                  Applied for {session?.position || session?.role || "this role"}
                  {profile?.experienceYears ? ` · ${profile.experienceYears} years' experience` : ""}
                </p>
                <div className="hmeta">
                  {session?.submitted_at && (
                    <span className="tag"><Icon id="cal" />Took the test {fmtDate(session.submitted_at)}</span>
                  )}
                  <span className="tag"><Icon id="doc" />Answered {model.answeredCount} of {model.questionCount} questions</span>
                  {profile?.avatarUrl && <span className="tag"><Icon id="shield" />Identity confirmed by photo</span>}
                </div>
              </div>
              <div className="sc">
                <div className="scv" style={{ color: V[scoreTone] }}>
                  {Math.round(model.percentage)}<span style={{ fontSize: 24 }}>%</span>
                </div>
                <div className="scl">Pass mark was {model.passMark}%</div>
                <span className={`pill ${P[scoreTone]}`}>
                  <Icon id={model.passed ? "check" : "alert"} />
                  {model.passed ? "Passed" : model.percentage >= model.passMark - 10 ? "Just below" : "Below the bar"}
                </span>
              </div>
            </div>
          </section>

          {/* ------------------------------------------------------- call */}
          {narrative && (
            <section id="call">
              <div className="call" style={{ borderTop: `4px solid ${BAR[scoreTone]}` }}>
                <p className="eyebrow" style={{ marginBottom: 11 }}><Icon id="bulb" />Our recommendation</p>
                <h2 className="callh">{narrative.headline}</h2>
                <p className="callp">{narrative.detail}</p>

                <div className="kpis">
                  <Kpi n={Math.round(model.percentage)} u="%" l="Scored" c={V[scoreTone]} />
                  <Kpi n={model.passMark} u="%" l="Needed to pass" />
                  {/* Marks, not percentage points, so this reads as the same
                      unit as the "N marks short" in the paragraph above. */}
                  <Kpi n={model.passed ? model.pointsClear : model.pointsShort} u="marks"
                    l={model.passed ? "Clear by" : "Short by"} c={model.passed ? V.good : V.mid} />
                  <Kpi n={model.correctCount} u={`/${model.questionCount}`} l="Answers correct" c={V.good} />
                  {model.secondsTaken != null && (
                    <Kpi n={Math.max(1, Math.round(model.secondsTaken / 60))} u="min"
                      l={session?.total_time ? `Of ${Math.round(session.total_time / 60)} min allowed` : "Time taken"} />
                  )}
                  <Kpi n={integrity} u="%" l="Honesty score" c="var(--cyan)" />
                </div>

                {(narrative.fitFor || narrative.fitAgainst) && (
                  <div className="fitgrid">
                    {narrative.fitFor && (
                      <div className="fit yes">
                        <span className="fi"><Icon id="check" /></span>
                        <div><b>Good fit if the job is...</b><span>{narrative.fitFor}</span></div>
                      </div>
                    )}
                    {narrative.fitAgainst && (
                      <div className="fit no">
                        <span className="fi"><Icon id="x" /></span>
                        <div><b>Not yet if the job needs...</b><span>{narrative.fitAgainst}</span></div>
                      </div>
                    )}
                  </div>
                )}
                {narrative.nextStep && (
                  <p style={{ fontSize: 13.5, color: "var(--text-3)", marginTop: 16, lineHeight: 1.55 }}>
                    <b style={{ color: "var(--text-2)" }}>Quickest way to settle it:</b> {narrative.nextStep}
                  </p>
                )}
              </div>
            </section>
          )}

          {/* ------------------------------------------------------ found */}
          {findings.length > 0 && (
            <section id="found">
              <div className="card pad">
                <p className="eyebrow"><Icon id="target" />What we found</p>
                <h2 className="sec">
                  {findings.length === 3 ? "Three things worth knowing"
                    : findings.length === 2 ? "Two things worth knowing" : "Worth knowing"}
                </h2>
                <p className="sub">Each one comes from an answer they actually gave.</p>
                <div className="pl">
                  {findings.map((f, i) => (
                    <div className="pi" key={i}>
                      <span className={`ic ${f.tone === "good" ? "good" : f.tone === "bad" ? "bad" : "mid"}`}>
                        <Icon id={IC[f.tone as Tone]} />
                      </span>
                      <div>
                        <h4>{f.title}</h4>
                        <p>{f.body}</p>
                        {f.max ? (
                          <div className="mini">
                            <span style={{ width: `${Math.max((f.points! / f.max) * 100, 2)}%`, background: BAR[f.tone as Tone] }} />
                          </div>
                        ) : null}
                      </div>
                      {f.max != null && (
                        <div className="lvl">
                          <div className="lvlbig" style={{ color: V[f.tone as Tone] }}>
                            {f.points}<span>/{f.max}</span>
                          </div>
                          <div className="lvlw" style={{ color: V[f.tone as Tone] }}>{bandFor((f.points! / f.max) * 100).word}</div>
                          <div className="lvlp">{f.caption}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ----------------------------------------------------- skills */}
          <section id="skills">
            <div className="card pad">
              <p className="eyebrow"><Icon id="chart" />Skills tested</p>
              <h2 className="sec">Score by skill</h2>
              <p className="sub">
                {model.skills.length === 1 ? "One skill was" : `${model.skills.length} skills were`} covered in this assessment.
              </p>

              <div className={full && model.skills.length >= 3 ? "split" : ""}>
                {full && model.skills.length >= 3 && <Radar skills={model.skills} />}
                <div className="pl">
                  {model.skills.map((s) => {
                    const tone = toneFor(s.pct) as Tone;
                    const blurb = skillBlurb(s.label);
                    return (
                      <div className="pi" key={s.label}>
                        <SkillTile skill={s.label} />
                        <div>
                          <h4>{s.label}</h4>
                          <p>{full
                            ? `${s.count} question${s.count === 1 ? "" : "s"}${blurb ? ` · ${blurb}` : ""}`
                            : blurb || `${s.count} question${s.count === 1 ? "" : "s"}`}</p>
                          <div className="mini">
                            <span style={{ width: `${Math.max(s.pct, 2)}%`, background: BAR[tone] }} />
                          </div>
                        </div>
                        <div className="lvl">
                          <div className="lvlbig" style={{ color: V[tone] }}>{Math.round(s.pct)}%</div>
                          <div className="lvlw" style={{ color: V[tone] }}>{bandFor(s.pct).word}</div>
                          <div className="lvlp">{s.points} / {s.max}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {full && (model.difficulty.length > 1 || model.secondsTaken != null) && (
                <div className="panels" style={{ marginTop: 22 }}>
                  {model.difficulty.length > 1 && (
                    <div className="panel">
                      <h4><Icon id="zap" />How they did as questions got harder</h4>
                      {model.difficulty.map((d) => (
                        <PRow key={d.label} label={`${cap(d.label)} · ${d.count} question${d.count === 1 ? "" : "s"}`}
                          pct={d.pct} value={`${Math.round(d.pct)}%`} title={`${d.points} of ${d.max} marks`} />
                      ))}
                      <p className="pnote">{difficultyNote(model)}</p>
                    </div>
                  )}
                  {model.secondsTaken != null && session?.total_time > 0 && (
                    <div className="panel">
                      <h4><Icon id="clock" />How they used their time</h4>
                      <PRow label="Time spent answering" scored={false}
                        pct={(model.secondsTaken / session.total_time) * 100}
                        value={formatDuration(model.secondsTaken)}
                        title={`${formatDuration(model.secondsTaken)} of ${Math.round(session.total_time / 60)} minutes allowed`} />
                      <p className="pnote">
                        Finished in <b>{formatDuration(model.secondsTaken)} of the {Math.round(session.total_time / 60)} minutes allowed</b>
                        {model.secondsTaken < session.total_time * 0.5 ? ", so this is not a candidate who ran out of time." : "."}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* ------------------------------------------------------ trust */}
          <section id="trust">
            <div className="card pad">
              <p className="eyebrow"><Icon id="shield" />Can you trust this result</p>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <h2 className="sec">{integrity >= 90 ? "Yes, and here is the proof" : "Mostly, with one thing to check"}</h2>
                <span className={`pill ${integrity >= 90 ? "p-pass" : "p-warn"}`}>
                  <Icon id={integrity >= 90 ? "check" : "alert"} />Honesty score {integrity}%
                </span>
              </div>
              <p className="sub">
                {shots.length ? "We watched the session and kept the evidence. Click any photo to enlarge." : "We watched the session throughout."}
              </p>

              <div className="pl" style={{ marginBottom: 20 }}>
                {profile?.avatarUrl && (
                  <Proof tone="good" ic="user" title="It was really them"
                    body="A photo was taken before the test began and matched to the invitation we emailed. That is the same face shown at the top of this report." />
                )}
                {codingRows.length > 0 && (
                  <Proof tone="good" ic="code" title="The code genuinely ran"
                    body="Their programming answer was executed on a real machine against real test data. They did not get credit for code that looks right; they got credit for code that worked." />
                )}
                {events.some((e) => e.serious) ? (
                  <Proof tone="mid" ic="alert" title="One thing to look at"
                    body={`${events.find((e) => e.serious)!.label} during the session. That is why the honesty score is ${integrity}% rather than 100%. It is not proof of cheating on its own, so we show you the record instead of deciding for you.`} />
                ) : (
                  <Proof tone="good" ic="shield" title="Nothing was flagged"
                    body="No behaviour during the session gave us cause for concern." />
                )}
              </div>

              {shots.length > 0 && <ShotSlider shots={shots} onOpen={setLightbox} />}

              {full && events.length > 0 && (
                <div style={{ marginTop: 22 }}>
                  <h4 style={{ fontSize: 13, marginBottom: 8 }}>Everything the proctor recorded</h4>
                  {events.map((e) => (
                    <div className="evt" key={e.key}>
                      <span className={`ei${e.serious ? " w" : ""}`}><Icon id={e.serious ? "alert" : "user"} /></span>
                      <span>{e.label}</span>
                      <span className="c">{e.count}</span>
                      <span className={`pill ${e.serious ? "p-warn" : "p-mute"}`}>{e.serious ? "Counted" : "No effect"}</span>
                    </div>
                  ))}
                  {noise > 0 && (
                    <p style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 11, lineHeight: 1.55 }}>
                      {noise} further background measurements (typing rhythm and inactivity sampling) ran on a timer and flagged nothing.
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* ---------------------------------------------------- answers */}
          {full && (
            <section id="answers">
              <div className="card pad">
                <p className="eyebrow"><Icon id="doc" />Every answer</p>
                <h2 className="sec">All {model.questionCount} questions, with their answers</h2>
                <p className="sub">Click a row to see the question and why it scored what it did.</p>
                {model.rows.map((row, i) => (
                  <QuestionRow key={row.question_id ?? i} row={row}
                    question={find(questions, row.question_id)}
                    answer={find(candidateAnswers, row.question_id, "question_id")}
                    inSession={inSession(row.question_id)}
                    open={openQ === row.question_id}
                    onToggle={() => setOpenQ(openQ === row.question_id ? null : row.question_id)} />
                ))}
              </div>
            </section>
          )}

          {/* ------------------------------------------------------- code */}
          {full && codingRows.length > 0 && (
            <section id="code">
              {codingRows.map((row) => (
                <CodeCard key={row.question_id} row={row}
                  question={find(questions, row.question_id)}
                  answer={find(candidateAnswers, row.question_id, "question_id")}
                  inSession={inSession(row.question_id)}
                  showHarness={showHarness} onToggle={() => setShowHarness((v) => !v)} />
              ))}
            </section>
          )}

          {/* ------------------------------------------------------- next */}
          <section id="next">
            <div className="acts">
              <div className="act primary">
                <h3><Icon id="cal" />Talk to {firstName}</h3>
                <p>Opens them in Talent Navigator with an interview request ready to send. Does not cost credits.</p>
                <button className="btn" type="button" onClick={() => setAsking("interview")}>
                  Schedule an interview<Icon id="chev" />
                </button>
              </div>
              <div className="act second">
                <h3><Icon id="msg" />Want a second opinion?</h3>
                <p>Ask our team to look deeper: extra checks, references, or a human review of this result. We will come back to you directly.</p>
                <button className="btn btn-outline" type="button" disabled={sent}
                  onClick={() => setAsking("support")}
                  style={sent ? { color: "var(--pass)", borderColor: "var(--pass)" } : undefined}>
                  {sent ? "Sent. We will be in touch" : <>Ask our team<Icon id="chev" /></>}
                </button>
              </div>
            </div>
          </section>
        </main>
      </div>

      <footer>
        <div className="wrap">
          <div style={{ maxWidth: "42ch", display: "flex", flexDirection: "column", gap: 6 }}>
            <p style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 18, fontWeight: 600, letterSpacing: "-.012em", lineHeight: 1.35 }}>
              Every engineer on Talent Navigator arrives with a report like this one.
            </p>
            <p style={{ fontSize: 13.5, color: "var(--text-2)", lineHeight: 1.55 }}>
              Skills tested and scored before you take a single call.
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start" }}>
            <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
              <a className="btn btn-primary" href={TALENT_NAVIGATOR} target="_blank" rel="noopener noreferrer">
                Browse vetted engineers<Icon id="chev" />
              </a>
              <a className="btn btn-outline" href={SKILLPROOF} target="_blank" rel="noopener noreferrer">
                Test your own shortlist
              </a>
            </div>
            <div className="flinks">
              <a href={TALENT_NAVIGATOR} target="_blank" rel="noopener noreferrer">Talent Navigator</a>
              <a href={SKILLPROOF} target="_blank" rel="noopener noreferrer">SkillProof</a>
              <a href="https://weremotedevs.com" target="_blank" rel="noopener noreferrer">WeRemoteDevs</a>
            </div>
            <p className="fnote">
              {report?.created_at ? fmtDate(report.created_at) : ""} · &copy; {new Date().getFullYear()} WeRemoteDevs
            </p>
          </div>
        </div>
      </footer>

      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(8,10,23,.74)", display: "grid", placeItems: "center", padding: 20 }}>
          <div className="card" style={{ maxWidth: 660, width: "100%", overflow: "hidden" }} onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.src} alt={lightbox.title} style={{ width: "100%", display: "block" }} />
            <div style={{ padding: "15px 19px 19px", display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
              <div>
                <h3 style={{ fontSize: 15.5 }}>{lightbox.title}</h3>
                <p style={{ fontSize: 13.5, color: "var(--text-2)", marginTop: 5, maxWidth: "54ch", lineHeight: 1.55 }}>{lightbox.note}</p>
              </div>
              <button className="btn btn-outline" type="button" onClick={() => setLightbox(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {asking && (
        <ContactDialog
          intent={asking}
          candidateName={firstName}
          onClose={() => setAsking(null)}
          onSubmit={async (details, spam) => {
            const intent = asking;
            await submitContact({ intent, details, spam, session, report, candidateId });
            setAsking(null);
            if (intent === "support") setSent(true);
            // Their request is recorded before they leave, so the team has the
            // details even if the candidate page needs a login first.
            else if (interviewUrl) window.location.assign(interviewUrl);
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ bits */

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
const find = (rows: any[], id: any, key = "id") => (rows ?? []).find((r) => r?.[key] === id) ?? null;

const Kpi = ({ n, u, l, c }: any) => (
  <div className="kpi">
    <span className="kn" style={c ? { color: c } : undefined}>{n}<i>{u}</i></span>
    <span className="kl">{l}</span>
  </div>
);

const Proof = ({ tone, ic, title, body }: any) => (
  <div className="pi">
    <span className={`ic ${tone}`}><Icon id={ic} /></span>
    <div><h4>{title}</h4><p>{body}</p></div>
  </div>
);

/**
 * A row in the difficulty or pace panel.
 *
 * `scored` says whether the percentage is a mark worth colouring. Time used is
 * not a score: painting a fast finish red said the opposite of what it means.
 */
const PRow = ({ label, pct, value, title, scored = true }: any) => {
  const tone = scored ? (toneFor(pct) as Tone) : null;
  return (
    <div className="prow" title={title}>
      <span>{label}</span>
      <span className="mb">
        <span style={{ width: `${Math.max(pct, 2)}%`, background: tone ? BAR[tone] : "var(--accent)" }} />
      </span>
      <span className="pc" style={{ color: tone ? V[tone] : "var(--text-2)" }}>{value}</span>
    </div>
  );
};

function difficultyNote(m: any) {
  const easy = m.difficulty.find((x: any) => x.label === "easy");
  const hard = m.difficulty.find((x: any) => x.label === "hard");
  if (easy && hard && easy.pct - hard.pct >= 30)
    return <><b>Solid basics that thin out under pressure.</b> This is what a mid-level engineer usually looks like, not a senior one.</>;
  if (hard && hard.pct >= 60)
    return <><b>They held up on the hard questions.</b> That is the part most candidates lose marks on.</>;
  return <>Scores held roughly level as the questions got harder.</>;
}

function buildShots(profile: any, events: any[]) {
  const out: any[] = [];
  if (profile?.avatarUrl) {
    out.push({
      src: profile.avatarUrl, title: "Before the test", when: "Identity confirmed", tone: "good",
      note: "Taken as the assessment started. A face was detected and matched to the invitation we emailed, then saved as the profile photo.",
    });
  }
  for (const e of events ?? []) {
    if (!e?.snapshot_url || out.some((o) => o.src === e.snapshot_url)) continue;
    const flagged = String(e.severity ?? "").toLowerCase() !== "info" && e.event_type !== "periodic";
    out.push({
      src: e.snapshot_url,
      title: flagged ? "Flagged moment" : "Mid-test",
      when: flagged ? String(e.event_type ?? "").replace(/_/g, " ") : "Routine check",
      tone: flagged ? "mid" : "neutral",
      note: flagged
        ? "This is what reduced the honesty score. It is shown rather than judged for you: seeing it on camera is not proof of anything on its own."
        : "One of the scheduled checks through the session. Nothing was flagged here; it is included so you see the whole record, not only the bad moments.",
    });
  }
  // No cap. The photos sit in a scrolling track now, so a long session adds to
  // the scroll instead of stretching the page, and hiding part of the record
  // would undercut the point of showing it.
  return out;
}

/* --------------------------------------------------------------- radar */

function Radar({ skills }: { skills: any[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const R = 78, CX = 170, CY = 142;
  const g = radarGeometry(skills, R, CX, CY);
  return (
    <div>
      <svg id="radar" viewBox="0 0 340 300" role="img"
        aria-label={`Skill chart: ${skills.map((s) => `${s.label} ${Math.round(s.pct)} percent`).join(", ")}`}>
        {g.rings.map((ring, i) => (
          <polygon key={i} points={ring} fill="none" stroke="var(--border)" strokeWidth={1} />
        ))}
        {skills.map((_, i) => {
          const p = g.at(i, R);
          return <line key={i} x1={CX} y1={CY} x2={p.x} y2={p.y} stroke="var(--border)" strokeWidth={1} />;
        })}
        <polygon points={g.shape} fill="var(--accent)" fillOpacity={0.2}
          stroke="var(--accent)" strokeWidth={2.5} strokeLinejoin="round" />
        {g.dots.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={hover === i ? 6 : 4} fill="var(--surface)"
              stroke="var(--accent)" strokeWidth={2.5} style={{ transition: "r .12s" }} />
            {/* A generous invisible target: the visible dot is too small to hit. */}
            <circle cx={p.x} cy={p.y} r={15} fill="transparent" style={{ cursor: "pointer" }}
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(i)} onBlur={() => setHover(null)} tabIndex={0}>
              <title>{`${skills[i].label}: ${skills[i].points} of ${skills[i].max} marks (${Math.round(skills[i].pct)}%)`}</title>
            </circle>
          </g>
        ))}
        {g.labels.map((l, i) => (
          <g key={i} opacity={hover === null || hover === i ? 1 : 0.45} style={{ transition: "opacity .12s" }}>
            {wrapLabel(l.label).map((line, k) => (
              <text key={k} x={l.x} y={l.y + 4 + k * 12} textAnchor={l.anchor as any}
                fill="var(--text-2)" fontSize={11} fontWeight={600}>{line}</text>
            ))}
            <text x={l.x} y={l.y + 4 + wrapLabel(l.label).length * 12} textAnchor={l.anchor as any}
              fill="var(--text-3)" fontSize={10.5} fontFamily="'JetBrains Mono',monospace">
              {skills[i].points}/{skills[i].max}
            </text>
          </g>
        ))}
      </svg>
      <p style={{ textAlign: "center", fontSize: 11.5, color: "var(--text-3)", marginTop: 4 }}>
        {hover !== null
          ? <b style={{ color: "var(--text-2)" }}>{skills[hover].label}: {skills[hover].points} of {skills[hover].max} marks ({Math.round(skills[hover].pct)}%)</b>
          : "Rings = 25 / 50 / 75 / 100%. Hover a point for its marks."}
      </p>
    </div>
  );
}

/** Two lines at most, broken on a space so no word is ever cut in half. */
function wrapLabel(label: string, max = 15): string[] {
  const s = String(label).trim();
  if (s.length <= max) return [s];
  const words = s.split(/\s+/);
  const lines: string[] = []; let cur = "";
  for (const w of words) {
    if (!cur) cur = w;
    else if ((cur + " " + w).length <= max) cur += " " + w;
    else { lines.push(cur); cur = w; }
    if (lines.length === 1 && cur.length > max) break;
  }
  if (cur) lines.push(cur);
  if (lines.length > 2) { lines.length = 2; lines[1] = lines[1].slice(0, max - 1) + "…"; }
  return lines;
}

/* ----------------------------------------------------------- questions */

function QuestionRow({ row, question, answer, inSession, open, onToggle }: any) {
  const pts = row.points_awarded ?? row.points_earned ?? 0;
  const gap = answer?.answer_text || answer?.code_submission ? null : missingAnswerStatus(row, inSession);
  const max = row.max_points ?? 0;
  const tone: Tone = max === 0 ? "neutral" : pts >= max ? "good" : pts > 0 ? "mid" : "bad";
  const title = question?.question || question?.question_text || row.question || questionTypeLabel(row.question_type);

  return (
    <details className="q" open={open}>
      <summary className="qh" onClick={(e) => { e.preventDefault(); onToggle(); }}>
        <span className={`qi ${tone === "good" ? "ok" : tone === "bad" ? "no" : "pt"}`}>
          <Icon id={IC[tone]} />
        </span>
        <span className="qt">
          <span className="qtx">{String(title).split("\n")[0]}</span>
          <span className="qtag">
            <span>{questionTypeLabel(row.question_type)}</span>
            {row.skill && <><span>·</span><span>{row.skill}</span></>}
            {row.difficulty && <><span>·</span><span>{cap(String(row.difficulty))}</span></>}
            {row.time_taken != null && <><span>·</span><span>{formatDuration(row.time_taken)}</span></>}
          </span>
        </span>
        <span className="qsc" style={{ color: V[tone] }}>{pts} / {max}</span>
        <Icon id="chev" className="i chev" />
      </summary>
      {open && (
        <div className="qb">
          {question?.question && (
            <div>
              <h5>The question</h5>
              <div className="box"><ReactMarkdown remarkPlugins={[remarkGfm]}>{String(question.question)}</ReactMarkdown></div>
            </div>
          )}
          {gap && (
            <div>
              <h5>Their answer</h5>
              <div className="box"><strong>{gap.headline}.</strong> {gap.detail}</div>
            </div>
          )}
          {Array.isArray(question?.options) && question.options.length > 0 ? (
            <div>
              <h5>Their options</h5>
              <div className="opts">
                {question.options.map((opt: string, i: number) => {
                  const chosen = same(opt, answer?.answer_text);
                  const correct = same(opt, question.correct_answer);
                  return (
                    <div className={`opt${correct ? " ok" : ""}`} key={i}
                      style={chosen && !correct ? { background: "var(--fail-soft)", borderColor: "var(--fail)" } : undefined}>
                      <span className="mk" style={chosen && !correct ? { background: "var(--fail-b)", color: "#fff" } : undefined}>
                        {String.fromCharCode(65 + i)}
                      </span>
                      <span style={{ flex: 1 }}>{opt}</span>
                      {(chosen || correct) && (
                        <span className="tg" style={{ color: correct ? "var(--pass)" : "var(--fail)" }}>
                          {chosen && correct ? "They chose this · Correct" : chosen ? "They chose this" : "Correct answer"}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : answer?.answer_text ? (
            <div>
              <h5>What they wrote</h5>
              <div className={`box ${tone === "good" ? "good" : tone === "bad" ? "bad" : "part"}`}>{answer.answer_text}</div>
            </div>
          ) : null}
          {question?.explanation && (
            <div>
              <h5>{pts >= max && max > 0 ? "Why that is right" : "What we were looking for"}</h5>
              <div className="box"><ReactMarkdown remarkPlugins={[remarkGfm]}>{String(question.explanation)}</ReactMarkdown></div>
            </div>
          )}
          {row.ai_feedback && (
            <div><h5>How it was marked</h5><div className="box">{String(row.ai_feedback)}</div></div>
          )}
        </div>
      )}
    </details>
  );
}

const same = (a: any, b: any) => {
  const n = (v: any) => String(v ?? "").toLowerCase().replace(/^[a-d][).]\s*/, "").replace(/\s+/g, " ").trim();
  return !!n(a) && n(a) === n(b);
};

/* ---------------------------------------------------------------- code */

function CodeCard({ row, question, answer, inSession, showHarness, onToggle }: any) {
  const review = row.code_review ?? null;
  const code = answer?.code_submission || answer?.answer_text || "";
  // The template for the language they actually wrote in. Taking whichever
  // language happened to be first would share no lines with their code, and the
  // card would then wrongly credit them with writing the template themselves.
  const byLang = question?.starter_code_by_language ?? null;
  const lang = answer?.code_language ?? null;
  const starter = question?.starter_code
    || (byLang && lang && byLang[lang])
    || (byLang && Object.values(byLang)[0])
    || "";
  const { authored, harness, harnessUntouched, fromTemplate } = splitSubmission(String(code), String(starter));
  const gap = code ? null : missingAnswerStatus(row, inSession);
  const written = authored.filter((l: any) => l.mine).length;
  let exec: any = row.execution_result ?? answer?.execution_result ?? null;
  if (typeof exec === "string") { try { exec = JSON.parse(exec); } catch { exec = null; } }
  const tests: any[] = Array.isArray(exec?.test_results) ? exec.test_results : [];
  const pts = row.points_awarded ?? row.points_earned ?? 0;

  return (
    <div className="card pad">
      <p className="eyebrow"><Icon id="code" />The coding task</p>
      <h2 className="sec">{String(question?.question || "Coding challenge").split("\n")[0]}</h2>
      {code && (
        <p className="sub">
          {fromTemplate
            ? "We gave them a starting template so they could focus on the problem instead of setup. Only the lines they wrote themselves are highlighted."
            : "They wrote this from an empty file. No starting template was given, so every line here is their own."}
        </p>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 15 }}>
        <span className={`pill ${row.max_points && pts >= row.max_points ? "p-pass" : pts > 0 ? "p-warn" : "p-fail"}`}>
          <Icon id="check" />Scored {pts} / {row.max_points}
        </span>
        {review?.overall_score != null && <span className="pill p-pass">Code review {review.overall_score} / 100</span>}
        {written > 0 && (
          <span className="pill p-mute">
            {fromTemplate ? "They added" : "They wrote"} {written} line{written === 1 ? "" : "s"}
          </span>
        )}
        {code && !fromTemplate && <span className="pill p-mute">Written from scratch</span>}
        {harnessUntouched && <span className="pill p-pass"><Icon id="check" />Left our template untouched</span>}
      </div>

      {code ? (
        <div className="cb">
          <div className="cs">
            <pre className="code">
              {authored.map((l: any) => (
                <span className={`ln${l.mine ? " mine" : " h"}`} key={l.n}>
                  <span className="g">{l.n}</span>{l.text || " "}
                </span>
              ))}
            </pre>
          </div>
          {harness.length > 0 && (
            <>
              <button className="htog" type="button" onClick={onToggle} aria-expanded={showHarness}>
                {showHarness ? "Hide the template lines" : `Show the ${harness.length} template lines we supplied`}
              </button>
              {showHarness && (
                <div className="cs">
                  <pre className="code">
                    {harness.map((l: any) => (
                      <span className="ln h" key={l.n}><span className="g">{l.n}</span>{l.text || " "}</span>
                    ))}
                  </pre>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="box"><strong>{gap!.headline}.</strong> {gap!.detail}</div>
      )}

      {tests.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <h4 style={{ fontSize: 14, marginBottom: 9, display: "flex", alignItems: "center", gap: 7 }}>
            <Icon id="check" />We ran their code: {tests.filter((t) => t.passed).length} of {tests.length} tests passed
          </h4>
          <div style={{ overflowX: "auto" }}>
            <table className="t">
              <thead><tr>{["Test", "We gave it", "Should return", "It returned", "Result"].map((h) => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {tests.map((t, i) => (
                  <tr key={i}>
                    <td>{t.test_case ?? i + 1}</td>
                    <td>{trunc(t.input)}</td>
                    <td>{trunc(t.expected)}</td>
                    <td>{trunc(t.actual)}</td>
                    <td style={{ color: t.passed ? "var(--pass)" : "var(--fail)", fontWeight: 700 }}>{t.passed ? "Pass" : "Fail"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {review && (
        <div style={{ marginTop: 20, paddingTop: 18, borderTop: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 9 }}>
            <h4 style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 7 }}><Icon id="bulb" />Our review of their code</h4>
            {review.overall_score != null && (
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 21, fontWeight: 700, color: "var(--pass)" }}>
                {review.overall_score}
              </span>
            )}
          </div>
          {review.summary && <p style={{ fontSize: 15, color: "var(--text-2)", lineHeight: 1.6, maxWidth: "64ch" }}>{review.summary}</p>}
          <div className="rev">
            <RevCol color="var(--pass)" ic="check" title="Done well" items={review.good_parts} />
            <RevCol color="var(--warn)" ic="alert" title="Could be better" items={review.improvements} />
            <RevCol color="var(--text-3)" ic="x" title="Problems found" items={review.bugs} empty="None." />
          </div>
        </div>
      )}
    </div>
  );
}

const RevCol = ({ color, ic, title, items, empty }: any) => {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!list.length && !empty) return null;
  return (
    <div className="revc">
      <h6 style={{ color }}><Icon id={ic} />{title}</h6>
      <ul>{list.length ? list.map((x: string, i: number) => <li key={i}>{x}</li>)
        : <li style={{ color: "var(--text-3)", listStyle: "none", marginLeft: -15 }}>{empty}</li>}</ul>
    </div>
  );
};

/**
 * The proctoring photos, as one horizontal track at every width.
 *
 * A grid re-flowed into more and more rows as sessions accumulated photos, so a
 * long session pushed the rest of the page down and, on a phone, pushed the
 * cards themselves out past the edge. A track holds its height no matter how
 * many photos there are: extra ones extend the scroll rather than the layout.
 */
const SHOTS_SHOWN = 12;

function ShotSlider({ shots: all, onOpen }: { shots: any[]; onOpen: (s: any) => void }) {
  const rail = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState({ start: true, end: true, index: 1 });
  // One session recorded 75 flagged moments. All of it is real evidence and
  // none of it is hidden, but a page does not open with 75 photos in it.
  const [expanded, setExpanded] = useState(false);
  const shots = expanded ? all : all.slice(0, SHOTS_SHOWN);
  const rest = all.length - shots.length;

  const sync = useCallback(() => {
    const el = rail.current;
    if (!el) return;
    const step = el.firstElementChild
      ? (el.firstElementChild as HTMLElement).offsetWidth + 13
      : el.clientWidth;
    setAt({
      start: el.scrollLeft <= 1,
      // A sub-pixel track width can leave a pixel behind at the far end.
      end: el.scrollLeft >= el.scrollWidth - el.clientWidth - 2,
      index: Math.min(shots.length, Math.round(el.scrollLeft / step) + 1),
    });
  }, [shots.length]);

  useEffect(() => {
    sync();
    const el = rail.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    // Rotating a phone changes how many fit, so the arrows have to be re-checked.
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sync]);

  const nudge = (dir: number) => {
    const el = rail.current;
    if (!el) return;
    const first = el.firstElementChild as HTMLElement | null;
    el.scrollBy({ left: dir * ((first?.offsetWidth ?? el.clientWidth) + 13), behavior: "smooth" });
  };

  const scrollable = !(at.start && at.end);

  return (
    <div className="shotwrap">
      {scrollable && (
        <div className="shothead">
          <span className="shotcount">
            Photo {at.index} of {all.length}. Swipe or use the arrows.
          </span>
          <div className="shotnav">
            <button className="back" type="button" onClick={() => nudge(-1)}
              disabled={at.start} aria-label="Previous photo"><Icon id="chev" /></button>
            <button type="button" onClick={() => nudge(1)}
              disabled={at.end} aria-label="Next photo"><Icon id="chev" /></button>
          </div>
        </div>
      )}
      <div className="shots" ref={rail} onScroll={sync}
        role="group" aria-label={`${shots.length} proctoring photos`}>
        {shots.map((s, i) => (
          <button className="shot" type="button" key={i} onClick={() => onOpen(s)}>
            <img src={s.src} alt={s.title} loading="lazy" />
            <span className="cap">
              <b style={{ color: V[s.tone as Tone] }}>{s.title}</b>
              <span>{s.when}</span>
            </span>
          </button>
        ))}
        {rest > 0 && (
          <button className="shot shotmore" type="button" onClick={() => setExpanded(true)}>
            <span className="n">+{rest}</span>
            <span>more photo{rest === 1 ? "" : "s"}</span>
          </button>
        )}
      </div>
    </div>
  );
}

const trunc = (v: any, n = 26) => {
  const s = String(v ?? "");
  return s.length > n ? s.slice(0, n) + "…" : s;
};

async function submitContact(payload: {
  intent: ContactIntent;
  details: ContactDetails;
  spam: { elapsedMs: number; honeypot: string };
  session: any;
  report: any;
  candidateId: string | null;
}) {
  const { supabase } = await import("@/integrations/supabase/client");
  const { data, error } = await supabase.functions.invoke("report-support-request", {
    body: {
      intent: payload.intent,
      contact: payload.details,
      elapsed_ms: payload.spam.elapsedMs,
      trap: payload.spam.honeypot,
      session_id: payload.session?.id,
      report_id: payload.report?.id,
      candidate_id: payload.candidateId,
      report_url: typeof window !== "undefined" ? window.location.href : null,
    },
  });
  // A rejection has to reach the form, otherwise it closes on a request that
  // never arrived and nobody ever follows up.
  if (error || data?.ok === false) throw new Error("request failed");
}
